/**
 * NIGHTBOUND - optional audio.
 *
 * Audio is deliberately optional: no file is required to exist, nothing
 * autoplays, and every failure is swallowed. The manager only ever plays after
 * the player has interacted with the page and has sound enabled in settings.
 */

import { audioCandidates } from '../../shared/assets/manifest'

type SoundKey = 'ui_click' | 'ui_confirm' | 'ui_error' | 'care' | 'level_up' | 'ambience_shelter'

class AudioManager {
  private enabled = true
  private unlocked = false
  private elements = new Map<SoundKey, HTMLAudioElement>()
  private ambience: HTMLAudioElement | null = null
  private ambiencePlaying = false

  /** Called from the first real user gesture. */
  unlock(): void {
    this.unlocked = true
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    if (!enabled) {
      this.stopAmbience()
      for (const element of this.elements.values()) element.pause()
    }
  }

  private element(key: SoundKey): HTMLAudioElement | null {
    if (typeof Audio === 'undefined') return null
    const existing = this.elements.get(key)
    if (existing) return existing
    const [src] = audioCandidates(key)
    if (!src) return null
    const element = new Audio(src)
    element.preload = 'none'
    element.volume = key === 'ambience_shelter' ? 0.28 : 0.5
    element.addEventListener('error', () => this.elements.delete(key))
    this.elements.set(key, element)
    return element
  }

  /** Fire and forget. Missing files are not an error. */
  play(key: SoundKey): void {
    if (!this.enabled || !this.unlocked) return
    const element = this.element(key)
    if (!element) return
    try {
      element.currentTime = 0
      void element.play().catch(() => {})
    } catch {
      /* ignored - audio is never required */
    }
  }

  startAmbience(): void {
    if (!this.enabled || !this.unlocked || this.ambiencePlaying) return
    if (!this.ambience) {
      if (typeof Audio === 'undefined') return
      const [src] = audioCandidates('ambience_shelter')
      if (!src) return
      this.ambience = new Audio(src)
      this.ambience.loop = true
      this.ambience.volume = 0.28
      this.ambience.addEventListener('error', () => {
        this.ambience = null
        this.ambiencePlaying = false
      })
    }
    this.ambiencePlaying = true
    void this.ambience.play().catch(() => {
      this.ambiencePlaying = false
    })
  }

  stopAmbience(): void {
    this.ambiencePlaying = false
    this.ambience?.pause()
  }
}

export const audio = new AudioManager()
